import { z } from 'zod';
import { CalendarDate } from './project.js';
/**
 * Change Request và phiên bản kế hoạch — APM-704, spec mục 8.
 *
 * ## Hai khái niệm, đừng trộn
 *
 * **CR** là một tờ giấy: ai đề xuất, đổi gì, vì sao, ai duyệt. **Version** là
 * một *bookmark trên trục event log* — nó không chứa dữ liệu kế hoạch, nó chỉ
 * ghi "kế hoạch tại đúng thời điểm này". Diff giữa hai version là tập event nằm
 * giữa hai bookmark (ADR-001, docs/data-model.md §2).
 */
export const ChangeRequestKind = z.enum(['scope', 'schedule', 'resource', 'budget', 'other']);
export const ChangeRequestStatus = z.enum([
    'draft',
    'submitted',
    'approved',
    /** Bị từ chối. Trạng thái cuối — muốn làm lại thì mở CR mới. */
    'rejected',
    /** Đã duyệt **và** đã chốt lại kế hoạch; đây là lúc `project_version` sinh ra. */
    'applied',
]);
export const CreateChangeRequestInput = z.object({
    title: z.string().trim().min(1).max(300),
    description: z.string().min(1).max(10_000),
    /**
     * Bắt buộc. Spec mục 8 liệt kê "lý do" ngang hàng với nội dung: một CR không
     * nói vì sao thì người duyệt chỉ có thể duyệt theo cảm tính, và sáu tháng sau
     * không ai trả lời được "vì sao hồi đó dời mốc này".
     */
    reason: z.string().min(1).max(10_000),
    kind: ChangeRequestKind.optional(),
    /** Task mà CR nêu tên. Hạt giống để dò ảnh hưởng lan truyền. */
    taskIds: z.array(z.uuid()).max(50).optional(),
});
/** Chỉ sửa được khi còn `draft` — đã gửi duyệt thì nội dung phải đứng yên. */
export const UpdateChangeRequestInput = z.object({
    title: z.string().trim().min(1).max(300).optional(),
    description: z.string().min(1).max(10_000).optional(),
    reason: z.string().min(1).max(10_000).optional(),
    kind: ChangeRequestKind.optional(),
    taskIds: z.array(z.uuid()).max(50).optional(),
});
export const DecideChangeRequestInput = z.object({
    /** Bắt buộc khi từ chối — người đề xuất cần biết phải sửa gì. */
    note: z.string().max(1_000).optional(),
});
export const ApplyChangeRequestInput = z.object({
    /** Nhãn của version sinh ra, vd "Dời mốc UAT sang tháng 10". */
    label: z.string().max(200).optional(),
});
// ── View ────────────────────────────────────────────────────────────────────
export const ChangeRequestView = z.object({
    id: z.uuid(),
    projectId: z.uuid(),
    /** Mã hiển thị `CR-3`. */
    code: z.string(),
    number: z.number().int().positive(),
    title: z.string(),
    description: z.string(),
    reason: z.string(),
    kind: ChangeRequestKind,
    status: ChangeRequestStatus,
    requestedBy: z.uuid(),
    requestedByName: z.string().nullable(),
    submittedAt: z.iso.datetime().nullable(),
    decidedBy: z.uuid().nullable(),
    decidedByName: z.string().nullable(),
    decidedAt: z.iso.datetime().nullable(),
    decisionNote: z.string().nullable(),
    appliedAt: z.iso.datetime().nullable(),
    /** Version sinh ra khi áp dụng. `null` khi CR chưa được áp dụng. */
    versionNo: z.number().int().positive().nullable(),
    /** Mã những task mà CR nêu tên. */
    taskKeys: z.array(z.string()),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
// ── Phân tích ảnh hưởng ─────────────────────────────────────────────────────
export const ImpactedTask = z.object({
    taskId: z.uuid(),
    taskKey: z.string(),
    title: z.string(),
    assigneeName: z.string().nullable(),
    startDate: CalendarDate.nullable(),
    dueDate: CalendarDate.nullable(),
    estimateHours: z.number().nullable(),
    /** Task nằm trên đường găng: dời nó là dời ngày kết thúc dự án. */
    onCriticalPath: z.boolean(),
    /** `0` = task CR nêu tên; `n` = cách hạt giống n bước phụ thuộc. */
    distance: z.number().int().nonnegative(),
    isMilestone: z.boolean(),
});
/**
 * Ảnh hưởng của một CR — spec mục 8, "liệt kê task/pha bị ảnh hưởng".
 *
 * Tính **tất định** từ đồ thị phụ thuộc, không phải do AI đoán: con số này là
 * cơ sở để duyệt hay từ chối, nên nó phải giải thích được và lặp lại được.
 * Skill `change_impact` đọc lại đúng bảng này rồi diễn đạt thành lời cảnh báo.
 */
export const ChangeImpact = z.object({
    changeRequestId: z.uuid(),
    /** Task CR nêu tên. */
    direct: z.array(ImpactedTask),
    /** Task bị kéo theo qua đồ thị phụ thuộc. */
    downstream: z.array(ImpactedTask),
    totals: z.object({
        taskCount: z.number().int().nonnegative(),
        estimateHours: z.number().nonnegative(),
        assigneeCount: z.number().int().nonnegative(),
        milestoneCount: z.number().int().nonnegative(),
        criticalCount: z.number().int().nonnegative(),
        earliestStart: CalendarDate.nullable(),
        latestDue: CalendarDate.nullable(),
    }),
    /** Đã cắt bớt vì quá nhiều task bị kéo theo. */
    truncated: z.boolean(),
});
/** Hình dạng `ai_suggestion.content` của skill `change_impact`. */
export const ChangeImpactSuggestion = z.object({
    summary: z.string().min(1).max(2_000),
    /** Điều dễ hỏng nếu duyệt CR này. Mỗi mục phải dẫn được về một task hoặc con số. */
    warnings: z.array(z.string().max(500)).max(8),
    /** Việc nên làm kèm theo nếu duyệt. */
    suggestedActions: z.array(z.string().max(500)).max(8),
    affectedTaskKeys: z.array(z.string().max(40)).max(50),
});
// ── Phiên bản kế hoạch ──────────────────────────────────────────────────────
export const ProjectVersionView = z.object({
    id: z.uuid(),
    projectId: z.uuid(),
    versionNo: z.number().int().positive(),
    label: z.string().nullable(),
    /** CR sinh ra version này. `null` ở v1 — bản gốc không cần lý do. */
    changeRequestCode: z.string().nullable(),
    /**
     * `event_log.id` mà version này đánh dấu — **chuỗi**, không phải number:
     * cột là `BIGINT UNSIGNED` và `JSON.parse` biến mọi số thành double.
     */
    eventLogId: z.string(),
    createdBy: z.uuid(),
    createdByName: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
export const PlanDiffField = z.object({
    field: z.enum(['title', 'startDate', 'dueDate', 'assignee', 'estimateHours', 'status']),
    before: z.string().nullable(),
    after: z.string().nullable(),
});
export const PlanDiffTask = z.object({
    taskId: z.uuid(),
    taskKey: z.string(),
    title: z.string(),
    change: z.enum(['added', 'removed', 'changed']),
    fields: z.array(PlanDiffField),
});
export const PlanDiffDependency = z.object({
    fromTaskKey: z.string(),
    toTaskKey: z.string(),
    change: z.enum(['added', 'removed']),
});
/**
 * Diff giữa hai phiên bản kế hoạch — spec mục 8.
 *
 * ⚠️ Là **diff**, không phải nhật ký: với mỗi trường chỉ giữ giá trị đầu và
 * giá trị cuối trong khoảng. Một task bị dời ngày ba lần rồi trả về chỗ cũ thì
 * không xuất hiện ở đây, và đó là đúng — người đọc muốn biết kế hoạch khác
 * nhau ở đâu, không phải ai đã bấm gì.
 */
export const PlanDiff = z.object({
    projectId: z.uuid(),
    from: ProjectVersionView,
    to: ProjectVersionView,
    tasks: z.array(PlanDiffTask),
    dependencies: z.array(PlanDiffDependency),
    /** Số event đã quét trong khoảng — để người đọc biết diff dựng từ bao nhiêu. */
    eventCount: z.number().int().nonnegative(),
    truncated: z.boolean(),
});
//# sourceMappingURL=change-request.js.map